import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Badge, EmptyState, PageLoading, TextInput, Toggle } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import type { LeaveType } from '../../types';

/**
 * The leave types master — the kinds of leave a rep can apply for.
 *
 * A managed list the mobile apply form picks from. Deactivating keeps a
 * type off the form without disturbing the leave already recorded against
 * it; a type nobody has used can be removed outright.
 */
export default function LeaveTypesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<LeaveType | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await api.get<LeaveType[]>('/leave/types'));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load leave types.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function setActive(t: LeaveType, isActive: boolean) {
    try {
      await api.patch(`/leave/types/${t.id}`, { isActive });
      setTypes((list) => list.map((x) => (x.id === t.id ? { ...x, isActive } : x)));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update that.');
    }
  }

  return (
    <>
      <PageHeader
        title="Leave types"
        subtitle="The kinds of leave your team can apply for. These appear on the app's leave form."
        actions={isAdmin ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> Add leave type
          </button>
        ) : undefined}
      />

      <div className="page col gap-16">
        {loading ? <PageLoading /> : types.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title="No leave types yet"
              body={isAdmin ? 'Add the kinds of leave you grant — Sick, Paid, Casual, Unpaid.' : 'Nothing here yet.'}
              action={isAdmin ? (
                <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                  Add a leave type
                </button>
              ) : undefined}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Leave type</th>
                    <th>Pay</th>
                    <th className="num">Used</th>
                    <th>On the form</th>
                    {isAdmin ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {types.map((t) => (
                    <tr key={t.id}>
                      <td className="primary">
                        <span className="row gap-8" style={{ alignItems: 'center' }}>
                          <span style={{
                            width: 12, height: 12, borderRadius: 3, flex: 'none',
                            background: t.color ?? 'var(--brand)',
                          }} />
                          {t.name}
                          {!t.isActive ? <Badge tone="neutral">Hidden</Badge> : null}
                        </span>
                      </td>
                      <td>{t.isPaid ? <Badge tone="success">Paid</Badge> : <Badge tone="neutral">Unpaid</Badge>}</td>
                      <td className="num">{t.requestCount ?? 0}</td>
                      <td>
                        {isAdmin ? (
                          <Toggle checked={t.isActive} label="" onChange={(v) => void setActive(t, v)} />
                        ) : (t.isActive ? 'Yes' : 'No')}
                      </td>
                      {isAdmin ? (
                        <td>
                          <button type="button" className="icon-btn" title="Edit"
                            onClick={() => setEditing(t)}>
                            <Icon name="edit" size={15} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing ? (
        <LeaveTypeDialog
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      ) : null}
    </>
  );
}

function LeaveTypeDialog({ type, onClose, onSaved }: {
  type: LeaveType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: type?.name ?? '',
    isPaid: type?.isPaid ?? true,
    color: type?.color ?? '#F15E22',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    setBusy(true);
    try {
      const body = { name: form.name.trim(), isPaid: form.isPaid, color: form.color || null };
      if (type) await api.patch(`/leave/types/${type.id}`, body);
      else await api.post('/leave/types', body);
      toast.success(type ? 'Saved.' : `${body.name} added.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!type) return;
    setBusy(true);
    try {
      const r = await api.del<{ deactivated: boolean; requestsUsing?: number }>(`/leave/types/${type.id}`);
      toast.success(r.deactivated
        ? `${type.name} is used on ${r.requestsUsing} request(s), so it was hidden rather than deleted.`
        : `${type.name} removed.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={type ? type.name : 'Add a leave type'}
      onClose={onClose}
      footer={(
        <>
          {type ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !form.name.trim()}
            onClick={() => void save()}>
            {type ? 'Save' : 'Add'}
          </button>
        </>
      )}
    >
      <div className="col gap-12">
        <TextInput label="Name" value={form.name} placeholder="Sick leave"
          onChange={(e) => set({ name: e.target.value })} />
        <div className="row gap-12" style={{ alignItems: 'center' }}>
          <div>
            <label className="small" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Colour</label>
            <input type="color" value={form.color} style={{ width: 48, height: 36, padding: 2 }}
              onChange={(e) => set({ color: e.target.value })} />
          </div>
          <div className="grow" style={{ paddingTop: 18 }}>
            <Toggle checked={form.isPaid} label="Paid leave (counts as worked time for pay)"
              onChange={(isPaid) => set({ isPaid })} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
